import React, {useEffect} from "react";
import {Button, Card, Descriptions, Spin, Statistic, Table, Tag} from "antd";
import {ArrowLeftOutlined} from "@ant-design/icons";
import {Coins} from "lucide-react";
import {useNavigate, useParams} from "react-router-dom";
import {useDispatch, useSelector} from "react-redux";
import {AdminLayout} from "../../components/layout/AdminLayout";
import {getGroupSavingsCategoryDetails} from "../../redux/slice/groupSavingsSlice";
import {AppDispatch, RootState} from "../../redux/store";
import {HeroHeader, formatNaira, FONTS, COLORS} from "../../designSystem";

const money = (value: any) => formatNaira(value);
const moneyCell = (value: any) => <span className="font-mono">{formatNaira(value)}</span>;
const monoValue = {fontFamily: FONTS.mono};
const dateText = (value?: string | null) => value ? new Date(value).toLocaleString() : "None";

function GroupSavingsCategoryDetails() {
    const {id} = useParams();
    const navigate = useNavigate();
    const dispatch: AppDispatch = useDispatch();
    const {token} = useSelector((state: RootState) => state.account);
    const {selectedCategory: category, detailLoading} = useSelector((state: RootState) => state.groupSavings);
    const isCurrentCategory = category && String(category.id) === String(id);

    useEffect(() => {
        if (token && id) {
            dispatch(getGroupSavingsCategoryDetails({token, categoryId: id}));
        }
    }, [token, id, dispatch]);

    if (detailLoading || !isCurrentCategory) {
        return (
            <AdminLayout>
                <div className="m-10"><Spin tip="Loading category..."/></div>
            </AdminLayout>
        );
    }

    return (
        <AdminLayout>
            <main className="h-full overflow-y-auto px-6 py-8 md:px-8" style={{background: COLORS.surface}}>
                <Button className="mb-4" icon={<ArrowLeftOutlined/>} onClick={() => navigate(-1)}>Back</Button>
                <HeroHeader
                    icon={Coins}
                    title={category.name}
                    subtitle={category.groupName}
                />

                <section className="mt-6 grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-4">
                    <Card size="small"><Statistic title="Total Contributed" valueStyle={monoValue} value={money(category.totalContributed)}/></Card>
                    <Card size="small"><Statistic title="Target Amount" valueStyle={monoValue} value={money(category.targetAmount)}/></Card>
                    <Card size="small"><Statistic title="Members" value={`${category.completedMemberCount || 0}/${category.memberCount || 0}`}/></Card>
                    <Card size="small"><Statistic title="Payments" value={category.contributionCount || 0}/></Card>
                </section>

                <Card className="mt-6" size="small">
                    <Descriptions bordered column={{xs: 1, md: 2}}>
                        <Descriptions.Item label="Type"><Tag>{category.type}</Tag></Descriptions.Item>
                        <Descriptions.Item label="Frequency"><Tag>{category.frequency}</Tag></Descriptions.Item>
                        <Descriptions.Item label="Created">{dateText(category.dateCreated)}</Descriptions.Item>
                        <Descriptions.Item label="Last Contribution">{dateText(category.lastContributionAt)}</Descriptions.Item>
                        <Descriptions.Item label="Description">{category.description || "No description"}</Descriptions.Item>
                    </Descriptions>
                </Card>

                <Card title="Member Contributions" className="mt-6">
                    <Table
                        rowKey="savingsMemberId"
                        dataSource={category.memberContributions || []}
                        columns={[
                            {title: "Member", dataIndex: "memberName", key: "memberName"},
                            {title: "Email", dataIndex: "memberEmail", key: "memberEmail"},
                            {title: "Contributed", dataIndex: "totalContributed", key: "totalContributed", render: moneyCell},
                            {title: "Payments", dataIndex: "contributionCount", key: "contributionCount"},
                            {title: "Last Payment", dataIndex: "lastContributionAt", key: "lastContributionAt", render: dateText},
                        ]}
                    />
                </Card>

                <Card title="Recent Payments" className="mt-6">
                    <Table
                        rowKey="id"
                        dataSource={category.recentContributions || []}
                        columns={[
                            {title: "Member", dataIndex: "memberName", key: "memberName"},
                            {title: "Email", dataIndex: "memberEmail", key: "memberEmail"},
                            {title: "Amount", dataIndex: "amountPaid", key: "amountPaid", render: moneyCell},
                            {title: "Date", dataIndex: "datePaid", key: "datePaid", render: dateText},
                            {title: "Reference", dataIndex: "transactionReference", key: "transactionReference"},
                        ]}
                    />
                </Card>
            </main>
        </AdminLayout>
    );
}

export default GroupSavingsCategoryDetails;
